// pages/ContactPage.jsx
// Sends contact form data via GET request to Google Apps Script.
// GET with URL params works reliably with no-cors — POST body is stripped by browsers in no-cors mode.

import React, { useState } from "react";
import { useTheme } from "../context/ThemeContext";

const SCRIPT_URL = process.env.REACT_APP_CONTACT_SCRIPT_URL ||
  "https://script.google.com/macros/s/AKfycbwh91lZtuNhmBuVlE82vyYZEGWaZu77eF201ws8JocEMLVTr4h3f47vp7jBV9RpHyg3/exec";

export default function ContactPage() {
  const { t } = useTheme();
  const [form,   setForm]   = useState({ name: "", email: "", subject: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [errMsg, setErrMsg] = useState("");

  const handleSend = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setErrMsg("Name, email and message are required."); return;
    }
    setStatus("sending"); setErrMsg("");

    try {
      // Use GET with URL params — this is the only reliable way with Apps Script + no-cors
      // POST body is silently dropped by browsers in no-cors mode
      const params = new URLSearchParams({
        name:    form.name.trim(),
        email:   form.email.trim(),
        subject: form.subject.trim() || "(no subject)",
        message: form.message.trim(),
      });

      await fetch(`${SCRIPT_URL}?${params.toString()}`, {
        method: "GET",
        mode:   "no-cors", // Apps Script doesn't send CORS headers — this is expected
      });

      // no-cors = opaque response, can't read status, but if fetch didn't throw = request reached Google
      setStatus("sent");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      console.error("Contact form error:", err);
      setStatus("error");
      setErrMsg("Failed to send. Please try again or email directly.");
    }
  };

  const inp = {
    width: "100%", boxSizing: "border-box", height: 40,
    padding: "0 12px", border: `0.5px solid ${t.border2}`,
    borderRadius: 9, fontSize: 13, color: t.text,
    fontFamily: "inherit", outline: "none", background: t.inputBg,
    transition: "border-color 0.15s",
  };

  return (
    <section style={{ maxWidth: 1100, margin: "0 auto", padding: "108px 32px 80px" }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 64, alignItems: "stretch" }}>

        <div>
          <span style={{ fontSize: 11, color: t.text3, letterSpacing: "0.12em", fontWeight: 600, textTransform: "uppercase", display: "block", marginBottom: 12 }}>Get in Touch</span>
          <h1 style={{ fontSize: 30, fontWeight: 700, color: t.text, margin: "0 0 14px", fontFamily: "'Playfair Display',Georgia,serif", letterSpacing: "-0.02em", lineHeight: 1.2 }}>
            Contact QuantSoc
          </h1>
          <p style={{ fontSize: 14, color: t.text2, lineHeight: 1.75, marginBottom: 32 }}>
            For problem errors, content suggestions, or collaboration inquiries.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            {[
              { l: "Problem errors",  d: "Report a mistake in a problem statement or solution" },
              { l: "New problems",    d: "Suggest a problem you'd like to see added" },
              { l: "Collaboration",   d: "Academic or editorial partnership inquiries" },
            ].map(({ l, d }) => (
              <div key={l} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#6b4fe0", marginTop: 6, flexShrink: 0 }} />
                <div>
                  <p style={{ fontSize: 13, fontWeight: 600, color: t.text, margin: "0 0 2px" }}>{l}</p>
                  <p style={{ fontSize: 13, color: t.text3, margin: 0, lineHeight: 1.5 }}>{d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ background: t.cardBg, border: `0.5px solid ${t.border}`, borderRadius: 18, padding: "32px", minHeight: 480, display: "flex", flexDirection: "column", justifyContent: "center" }}>
          {status === "sent" ? (
            <div style={{ textAlign: "center", padding: "40px 0" }}>
              <div style={{ width: 52, height: 52, borderRadius: "50%", background: "#edf7f1", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M4 12l6 6L20 6" stroke="#2d7a4f" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <p style={{ fontSize: 16, fontWeight: 600, color: t.text, margin: "0 0 8px" }}>Message sent!</p>
              <p style={{ fontSize: 13, color: t.text3, margin: "0 0 20px" }}>We'll get back to you shortly.</p>
              <button onClick={() => setStatus("idle")}
                style={{ background: t.bg2, color: t.text2, border: `0.5px solid ${t.border}`, borderRadius: 8, padding: "8px 18px", fontSize: 13, cursor: "pointer" }}>
                Send another
              </button>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 16, flex: 1 }}>
              {[
                ["name",    "text",  "NAME",    "Your name"],
                ["email",   "email", "EMAIL",   "you@example.com"],
                ["subject", "text",  "SUBJECT", "e.g. Problem QS-002 error"],
              ].map(([k, tp, lbl, ph]) => (
                <div key={k}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: t.text2, letterSpacing: "0.05em", display: "block", marginBottom: 6 }}>{lbl}</label>
                  <input type={tp} placeholder={ph} value={form[k]}
                    onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
                    style={inp}
                    onFocus={(e) => (e.target.style.borderColor = "#6b4fe0")}
                    onBlur={(e)  => (e.target.style.borderColor = t.border2)}
                  />
                </div>
              ))}
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: t.text2, letterSpacing: "0.05em", display: "block", marginBottom: 6 }}>MESSAGE</label>
                <textarea placeholder="Your message…" value={form.message}
                  onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                  style={{ ...inp, height: "auto", minHeight: 110, padding: "10px 12px", resize: "vertical", lineHeight: 1.6 }}
                  onFocus={(e) => (e.target.style.borderColor = "#6b4fe0")}
                  onBlur={(e)  => (e.target.style.borderColor = t.border2)}
                />
              </div>

              {errMsg && <p style={{ fontSize: 12, color: "#c0392b", margin: 0 }}>{errMsg}</p>}

              <button onClick={handleSend} disabled={status === "sending"}
                style={{ background: "#6b4fe0", color: "#fff", border: "none", borderRadius: 10, padding: "12px 24px", fontSize: 14, fontWeight: 600, cursor: status === "sending" ? "not-allowed" : "pointer", opacity: status === "sending" ? 0.7 : 1, alignSelf: "flex-start", transition: "opacity 0.2s" }}>
                {status === "sending" ? "Sending…" : "Send Message"}
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
